/**
 * Centralized A/B Testing Experiment Configuration
 * 
 * Fetches experiment config from Edge Config REST API for client-side usage
 */

import { track } from '@vercel/analytics';
import { getTestBucket, checkUrlOverride, forceTestBucket } from './abtest';

// Cache for Edge Config to avoid repeated fetches
let edgeConfigCache = null;
let cacheTimestamp = 0;
const CACHE_TTL = 60000; // 1 minute cache

/**
 * Get experiment configuration from Edge Config REST API
 * @returns {Promise<Object|null>} Experiment configurations or null if unavailable
 */
export async function getExperimentsConfig() {
  // Check cache first
  const now = Date.now();
  if (edgeConfigCache && (now - cacheTimestamp) < CACHE_TTL) {
    return edgeConfigCache;
  }

  // Get connection string from env (with REACT_APP_ prefix for client-side)
  const edgeConfigUrl = process.env.REACT_APP_EDGE_CONFIG;
  
  if (!edgeConfigUrl) {
    if (process.env.REACT_APP_ENABLE_LOGGING === 'true') {
      console.warn('[Edge Config] REACT_APP_EDGE_CONFIG not set, no A/B testing will run');
    }
    return null;
  }

  try {
    // Parse connection string to build REST API URL
    // Format: https://edge-config.vercel.com/{configId}?token={token}
    const url = new URL(edgeConfigUrl);
    const configId = url.pathname.split('/')[1];
    const token = url.searchParams.get('token');
    
    // Build REST API URL to fetch specific item
    const itemUrl = `https://edge-config.vercel.com/${configId}/item/experiments?token=${token}`;
    
    const response = await fetch(itemUrl);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const edgeConfig = await response.json();
    
    if (edgeConfig) {
      edgeConfigCache = edgeConfig;
      cacheTimestamp = now;
      
      if (process.env.REACT_APP_ENABLE_LOGGING === 'true') {
        console.log('[Edge Config] Loaded experiments:', edgeConfigCache);
      }
      
      return edgeConfigCache;
    }
  } catch (error) {
    // Edge Config not available or error occurred
    if (process.env.REACT_APP_ENABLE_LOGGING === 'true') {
      console.warn('[Edge Config] Failed to load experiments, no A/B testing will run:', error.message);
    }
  }

  // Return null if Edge Config unavailable - no experiment runs
  return null;
}

/**
 * Get a specific experiment configuration
 * @param {string} experimentName - Name of the experiment
 * @returns {Promise<Object|null>} Experiment config or null if not found/unavailable
 */
export async function getExperimentConfig(experimentName) {
  const experiments = await getExperimentsConfig();
  if (!experiments) return null; // Edge Config failed
  return experiments[experimentName] || null;
}

/**
 * Get the variant name for a given experiment and bucket
 * @param {string} experimentName - Name of the experiment
 * @param {'A' | 'B'} bucket - Bucket assignment
 * @param {Object} experimentConfig - Optional experiment config (to avoid extra fetch)
 * @returns {string} Variant name
 */
export function getVariantName(experimentName, bucket, experimentConfig = null) {
  if (!experimentConfig) return 'unknown';
  const experiment = experimentConfig;
  if (!experiment) return 'unknown';
  return experiment.variants[bucket]?.name || 'unknown';
}

/**
 * Get the variant display name for a given experiment and bucket
 * @param {string} experimentName - Name of the experiment
 * @param {'A' | 'B'} bucket - Bucket assignment
 * @param {Object} experimentConfig - Optional experiment config (to avoid extra fetch)
 * @returns {string} Display name
 */
export function getVariantDisplayName(experimentName, bucket, experimentConfig = null) {
  if (!experimentConfig) return 'Unknown';
  const experiment = experimentConfig;
  if (!experiment) return 'Unknown';
  return experiment.variants[bucket]?.displayName || 'Unknown';
}

/**
 * Track an experiment view event
 * @param {string} experimentName - Name of the experiment
 * @param {'A' | 'B'} bucket - Bucket assignment
 * @param {boolean} isNew - Whether this is a new assignment
 * @param {Object} additionalProps - Additional properties to track
 */
export function trackExperimentView(experimentName, bucket, isNew, additionalProps = {}) {
  const variantName = getVariantName(experimentName, bucket);
  
  track('experiment_view', {
    experimentName,
    variant: bucket,
    variantName,
    isNewAssignment: isNew,
    timestamp: Date.now(),
    ...additionalProps
  });

  // Also log to console for debugging
  if (process.env.REACT_APP_ENABLE_LOGGING === 'true') {
    console.log('[Experiment View]', {
      experiment: experimentName,
      variant: `${bucket} (${variantName})`,
      isNew
    });
  }
}

/**
 * Track a custom experiment event with variant context
 * @param {string} experimentName - Name of the experiment
 * @param {string} eventType - Type of event (e.g., 'homepage_scrolled')
 * @param {Object} properties - Event properties
 */
export function trackExperiment(experimentName, eventType, properties = {}) {
  const { bucket } = getTestBucket(experimentName);
  const variantName = getVariantName(experimentName, bucket);
  
  track(eventType, {
    experimentName,
    variant: bucket,
    variantName,
    timestamp: Date.now(),
    ...properties
  });

  // Log to console for debugging
  if (process.env.REACT_APP_ENABLE_LOGGING === 'true') {
    console.log(`[${eventType}]`, {
      experiment: experimentName,
      variant: `${bucket} (${variantName})`,
      ...properties
    });
  }
}

/**
 * Get all active experiments (filtered by enabled flag)
 * @returns {Promise<Object>} All experiment configurations that are enabled
 */
export async function getActiveExperiments() {
  const experiments = await getExperimentsConfig();
  if (!experiments) return {}; // No experiments if Edge Config failed
  
  const activeExperiments = {};
  
  Object.keys(experiments).forEach(key => {
    const experiment = experiments[key];
    // Check if experiment has an enabled flag
    const isEnabled = experiment.enabled !== false; // Enabled by default
    
    if (isEnabled) {
      activeExperiments[key] = experiment;
    }
  });
  
  return activeExperiments;
}

/**
 * Check if an experiment is active and enabled
 * @param {string} experimentName - Name of the experiment
 * @returns {Promise<boolean>} Whether the experiment is active and enabled
 */
export async function isExperimentActive(experimentName) {
  const experiment = await getExperimentConfig(experimentName);
  if (!experiment) return false;
  
  // Check enabled flag (default to true if not specified)
  return experiment.enabled !== false;
}

/**
 * Get experiment (async version for Edge Config)
 * @param {string} experimentName - Name of the experiment
 * @returns {Promise<Object|null>} Experiment configuration or null if not found
 */
export async function getExperiment(experimentName) {
  return await getExperimentConfig(experimentName);
}

export default {
  getExperimentsConfig,
  getExperimentConfig,
  getVariantName,
  getVariantDisplayName,
  trackExperimentView,
  trackExperiment,
  getActiveExperiments,
  isExperimentActive,
  getExperiment
};
